import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Plan } from '../../../Plan/Plan';
import { PlansIds, PRODUCTS, SUBSCRIPTION_PERIOD } from 'constants/config';
import {
  StyledDesktopWrapper,
  StyledH1,
  StyledPlansContainer,
  StyledPlansSection,
  StyledPricingPage,
} from './PricingPageContentStyles';
import { PlansOptionsTable } from '../../../PlansOptionsTable/PlansOptionsTable';
import { TrialProposal } from '../../../TrialProposal/TrialProposal';
import { getSelectedPlan, sendChoosePlanRequest } from './PricingPageService';
import * as fastspring from '../../../../services/fastspringService';
import { FullScreenLoader } from '../../../FullScreenLoader';
import { UpgradeConfirmationPopup } from 'components/UpgradeConfirmationPopup';
import { PopupModeService } from 'services/popupModeService';
import { PaymentSwitcher } from 'components/PaymentSwitcher/PaymentSwitcher';
import { useConfirmationPopup } from './useConfirmationPopup';
import { useMaxDiscount } from 'common/useMaxDiscount';
import { withPromoHOC } from 'components/Plan/withPromoHOC';
import { FAQ } from 'components/FAQ';
import { convertUserToExperimentUser, getExperimentsList } from 'services/experiments';

function getPlanProps(
  {
    plan,
    interval,
    user,
    isAppSumo,
    isAnnuallyPlan,
    isTrial,
    prices,
    props,
  },
  isCompactMode = false,
) {
  const { planId, paymentPlanPaths } = props;
  const { email, id: userId } = user || {};
  // omit youtube brand account emails
  const userEmail = email && !/@pages.plusgoogle.com$/.test(email) ? email : null;
  const isCurrentPlan = plan === planId;
  const isCurrentInterval = (
    isAnnuallyPlan
      ? [SUBSCRIPTION_PERIOD.YEARLY, SUBSCRIPTION_PERIOD.LIFETIME]
      : [SUBSCRIPTION_PERIOD.MONTHLY]
  ).includes(interval);
  const isAuthorized = Boolean(user);
  const priceData = prices.getPrice({ paymentPlanPaths }) || {};
  const currencySymbol = prices.currencySymbol || '$';
  let planPaymentPath = paymentPlanPaths
    ? paymentPlanPaths[isAnnuallyPlan ? SUBSCRIPTION_PERIOD.YEARLY : SUBSCRIPTION_PERIOD.MONTHLY]
    : null;

  return {
    ...props,
    currentUserPlan: plan,
    currentPlanInterval: interval,
    isAppSumo,
    isAnnuallyPlan,
    isCurrentPlan,
    isCurrentInterval,
    isAuthorized,
    isTrial,
    isCompactMode,
    planPaymentPath,
    userEmail,
    userId,
    currencySymbol,
    user,
    ...priceData,
  };
}

export const PricingPageContent = withPromoHOC(({ user, loading, promo }) => {
  const [isAnnuallyPlan, setAnnualPlan] = useState(true);
  const [loaderVisible, showLoader] = useState(false);
  const [confirmationPopupState, confirmationPopupActions] = useConfirmationPopup();
  const [prices, setPrices] = useState({ getPrice: () => ({}) });
  const [_, setExperiments] = useState({});

  const maxDiscountData = useMaxDiscount(promo.getDiscount, PRODUCTS, user);
  const maxDiscount = `${maxDiscountData.currencySymbol}${maxDiscountData.discount}`;
  const toggleAnnually = (value) => setAnnualPlan(value);
  const { subscription: { plan = null, trial, interval } = {}, appsumo: isAppSumo = false } =
    user || { appsumo: true };

  const isTrial = Boolean(trial);
  const isPopupMode = PopupModeService.isPopupMode();

  useEffect(() => {
    try {
      if (!user) return;
      const experimentsUser = convertUserToExperimentUser(user);
      getExperimentsList(experimentsUser).then(setExperiments).catch(e => console.error(e));
    } catch (err) {
      console.error(err);
    }
  }, [user])

  useEffect(() => {
    if (loading) return;

    (async function request() {
      try {
        const plansPrices = await fastspring.getPlansPrices();
        setPrices(plansPrices);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [loading]);

  const choosePlan = useCallback(
    (planId) => {
      return sendChoosePlanRequest(planId, isAnnuallyPlan, !PopupModeService.isPopupMode());
    },
    [isAnnuallyPlan],
  );

  return (
    <React.Fragment>
      <Helmet>
        <meta
          name="description"
          content="Choose the best Be.Live plan & pricing that fits your budget and business needs"
        />
        <title>Be.Live Pricing</title>
      </Helmet>
      {loaderVisible && <FullScreenLoader />}
      {confirmationPopupState.visible && (
        <UpgradeConfirmationPopup
          onCancel={confirmationPopupActions.close}
          onProceed={confirmationPopupActions.confirm}
        />
      )}
      <StyledPricingPage>
        <StyledPlansSection>
          <StyledH1 withShadow textAlign="center">
            Choose the Plan That Matches Your Goals
          </StyledH1>
          {!user && <TrialProposal />}
          <PaymentSwitcher
            short
            enabled={isAnnuallyPlan}
            maxDiscount={maxDiscount}
            toggle={toggleAnnually}
          />
          <StyledPlansContainer>
            {PRODUCTS.map((props) => {
              const newProps = getPlanProps({
                plan,
                user,
                isAppSumo,
                isAnnuallyPlan,
                isTrial,
                prices,
                props,
                interval,
              });

              const discount = promo.getDiscount(
                { planId: plan, interval },
                {
                  planId: props.planId,
                  interval: isAnnuallyPlan
                    ? SUBSCRIPTION_PERIOD.YEARLY
                    : SUBSCRIPTION_PERIOD.MONTHLY,
                },
              );
              const coupon = discount > 0 ? promo.coupon : '';

              return (
                <Plan
                  mobileOnly={props.mobileOnly}
                  key={props.name}
                  choosePlan={choosePlan}
                  loading={loading}
                  showLoader={showLoader}
                  openConfirmationPopup={confirmationPopupActions.open}
                  promo={{ discount, coupon }}
                  {...newProps}
                />
              );
            })}
          </StyledPlansContainer>
        </StyledPlansSection>
        {!isPopupMode && (
          <>
            {/*<StyledDesktopWrapper>*/}
            {/*  <AmazonPlan loading={loading} prices={prices} />*/}
            {/*</StyledDesktopWrapper>*/}
            <StyledDesktopWrapper>
              <PlansOptionsTable products={PRODUCTS} />
            </StyledDesktopWrapper>
            <StyledDesktopWrapper>
              <FAQ />
            </StyledDesktopWrapper>
          </>
        )}
      </StyledPricingPage>
    </React.Fragment>
  );
});

PricingPageContent.propTypes = {
  user: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  loading: PropTypes.bool.isRequired,
};
